import React, { useEffect, useRef, useState } from 'react';

/**
 * Premium custom cursor — ring + dot with smooth lerp follow + hover state.
 * - Outer ring eases toward the mouse (lerp)
 * - Inner dot tracks the mouse exactly
 * - Expands + flips colors when hovering interactive elements
 * - Hidden on touch devices
 */
const Cursor = () => {
  const ringRef = useRef(null);
  const dotRef = useRef(null);
  const target = useRef({ x: 0, y: 0 });
  const pos = useRef({ x: 0, y: 0 });
  const [hovering, setHovering] = useState(false);
  const [hidden, setHidden] = useState(true);
  const [clicking, setClicking] = useState(false);

  useEffect(() => {
    // Skip on touch / coarse pointer devices
    const mq = window.matchMedia('(pointer: fine)');
    if (!mq.matches) return;

    const onMove = (e) => {
      target.current.x = e.clientX;
      target.current.y = e.clientY;
      if (hidden) setHidden(false);
      if (dotRef.current) {
        dotRef.current.style.transform = `translate3d(${e.clientX}px, ${e.clientY}px, 0) translate(-50%, -50%)`;
      }
    };

    const onEnter = () => setHidden(false);
    const onLeave = () => setHidden(true);
    const onDown = () => setClicking(true);
    const onUp = () => setClicking(false);

    const INTERACTIVE =
      'a, button, [role="button"], [role="menuitem"], input, textarea, select, label, [data-cursor="hover"]';
    const onOver = (e) => {
      if (e.target.closest && e.target.closest(INTERACTIVE)) setHovering(true);
    };
    const onOut = (e) => {
      if (e.target.closest && e.target.closest(INTERACTIVE)) setHovering(false);
    };

    window.addEventListener('mousemove', onMove, { passive: true });
    window.addEventListener('mouseenter', onEnter);
    document.addEventListener('mouseleave', onLeave);
    window.addEventListener('mousedown', onDown);
    window.addEventListener('mouseup', onUp);
    document.addEventListener('mouseover', onOver);
    document.addEventListener('mouseout', onOut);

    let raf;
    const tick = () => {
      pos.current.x += (target.current.x - pos.current.x) * 0.22;
      pos.current.y += (target.current.y - pos.current.y) * 0.22;
      if (ringRef.current) {
        ringRef.current.style.transform = `translate3d(${pos.current.x}px, ${pos.current.y}px, 0) translate(-50%, -50%)`;
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);

    document.documentElement.classList.add('cursor-none');

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseenter', onEnter);
      document.removeEventListener('mouseleave', onLeave);
      window.removeEventListener('mousedown', onDown);
      window.removeEventListener('mouseup', onUp);
      document.removeEventListener('mouseover', onOver);
      document.removeEventListener('mouseout', onOut);
      document.documentElement.classList.remove('cursor-none');
    };
  }, [hidden]);

  const ringClass = `arv-cursor-ring ${hovering ? 'is-hover' : ''} ${clicking ? 'is-click' : ''} ${hidden ? 'is-hidden' : ''}`;
  const dotClass = `arv-cursor-dot ${hovering ? 'is-hover' : ''} ${hidden ? 'is-hidden' : ''}`;

  return (
    <>
      <div ref={ringRef} className={ringClass} aria-hidden="true" />
      <div ref={dotRef} className={dotClass} aria-hidden="true" />
    </>
  );
};

export default Cursor;
